import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TableSearchComponent } from '../../shared/table-search/table-search.component';
import { TableComponent } from '../../shared/table/table.component';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { FileManagement, ResponseFile } from '../../interfaces/filemanagement';
import { RowData } from '../../shared/interfaces/table.data';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ModalDialogComponent } from '../../shared/modal-dialog/modal-dialog.component';
import { UserService } from '../../services/user/user.service';
import { FileManagementService } from '../../services/file-management/file-management.service';
import * as fileConstants from './../../shared/constants/filemanagementconstants';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { environment } from '../../../environments/environment';
import { MultiFileUploadModalComponent } from '../../shared/multi-file-upload-modal/multi-file-upload-modal.component';
import { UploadType } from '../../services/upload/upload-type';
import { MultiUploadModalData } from '../../shared/interfaces/modal.data';
import { SharePopupComponent } from '../../shared/share-popup/share-popup.component';
import { FileUploadStateService } from '../../file-upload-state.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-file-management',
  standalone: true,
  imports: [TableSearchComponent, TableComponent, ButtonsComponent, AsyncPipe],
  templateUrl: './file-management.component.html',
  styleUrl: './file-management.component.css',
})
export class FileManagementComponent {
  uploadUrl = `${environment.apiBaseUrl}/api/v1/buckets/upload/fasta-file`;
  dataSource: MatTableDataSource<RowData<FileManagement>> = new MatTableDataSource<RowData<FileManagement>>([]);
  btnNameShare: string = $localize`Share`;
  btnName: string = $localize`Upload Fasta File`;
  btnMatStroked = 'mat-stroked-button';
  btnStyleStroked = 'btn-stroked';
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  btnMatFlat: string = 'mat-flat-button';

  displayedColumnLabels: string[] = [fileConstants.FileName, fileConstants.UploadedOn];
  displayedColumns = fileConstants.displayedColumns;

  showCheckboxInTable: boolean = false;

  showActionBtnsInPagination: boolean = true;
  showDeleteButton = true;
  showActionColumn = true;
  bucketName: string = '';
  selectedFileItems: FileManagement[] = [];
  private uploadCompleteSubscription: Subscription | undefined;

  private userService = inject(UserService);
  readonly user$ = this.userService.user$;

  constructor(
    private dialog: MatDialog,
    private fileService: FileManagementService,
    private messageModal: MessageModalService,
    private fileUploadStateService: FileUploadStateService
  ) {}

  ngOnInit() {
    this.loadData();
    this.uploadCompleteSubscription = this.fileUploadStateService.allUploadsCompleted$.subscribe(() => {
      this.loadData();
    });
  }

  loadData() {
    this.fileService.getFiles().subscribe({
      next: (response: any) => {
        if (response.success && Array.isArray(response.files)) {
          const rowData: RowData<FileManagement>[] = response.files.map((item: ResponseFile) => ({
            data: {
              fileName: item.file_name,
              fullPath: item.full_path,
              uploadedOn: item.time_created,
              size: item.size,
            },
            selected: false,
          }));
          this.dataSource = new MatTableDataSource<RowData<FileManagement>>(rowData);
          this.dataSource.filterPredicate = this.createFilterPredicate();
        } else {
          console.error('Error fetching files:', response.message || 'Invalid data structure');
          this.messageModal.openModal($localize`Error`, $localize`${response.message}`);
        }
      },
      error: (err) => {
        console.error('Error while fetching the file list.', err);
        this.messageModal.openModal($localize`Error`, $localize`Error while fetching the file list.`);
      },
    });
  }

  onFilterChanged(filterValue: string): void {
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  public createFilterPredicate(): (item: RowData<FileManagement>, filter: string) => boolean {
    return (item: RowData<FileManagement>, filter: string) => {
      const terms = filter.trim().toLowerCase().split(/\s+/);

      const dataValues = Object.entries(item.data)
        .filter(([, val]) => val != null)
        .map(([key, val]) => {
          if (typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))) {
            // Assume it's an ISO date string -> extract date part
            return val.split('T')[0].toLowerCase();
          }
          return val.toString().toLowerCase();
        });

      return terms.every((term) => dataValues.some((value) => value.includes(term)));
    };
  }

  openFileUploadModal(): void {
    const dialogRef = this.dialog.open<MultiFileUploadModalComponent, MultiUploadModalData>(
      MultiFileUploadModalComponent,
      {
        // hasBackdrop: true,              // 👈 No backdrop
        // panelClass: 'floating-dialog',   // 👈 Custom CSS for positioning
        // position: { top: '100px', right: '100px' }, // Optional custom position
        width: '50rem',
        disableClose: true,
        data: {
          title: $localize`File Upload`,
          description: $localize`Select and upload the files of your choice`,
          primaryButtonLabel: $localize`Done`,
          secondaryButtonLabel: $localize`Cancel`,
          showPrimaryButton: true,
          showSecondaryButton: false,
          inputFileName: false,
          inputFileDescription: false,
          files: this.dataSource.filteredData ? this.dataSource.filteredData : [],
          uploadConfig: {
            featureType: UploadType.ReferenceFileManagement,
            acceptedFileTypes: '.fasta, .vcf, .bed',
            maxFileSize: 10000,
            useChunking: true,
            chunkSize: 16 * 1024 * 1024,
            apiUrl: this.uploadUrl,
            dictDefaultMessage: 'Drag instrument run files here or click to browse',
          },
        },
      }
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadData();
      }
    });
  }

  openDeletePopup(event: FileManagement): void {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      data: {
        title: $localize`Delete File?`,
        description: $localize`Once deleted, <strong> ${event.fileName} </strong> will no longer be available.`,
        primaryButtonLabel: $localize`Yes, Delete`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true, // Show primary button
        showSecondaryButton: true, // Secondary button is always shown
      },
    });
    const filePath = `${event.fullPath}`;
    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.handleDeleteFile(modalInstance, filePath);
    });
  }

  handleDeleteFile(modalInstance: ModalDialogComponent, filePath: string) {
    this.deleteFile(filePath).subscribe({
      next: (deleteFile: any) => {
        if (deleteFile.success) {
          this.loadData();
          modalInstance.closeModal(true);
        } else {
          modalInstance.showError($localize`${deleteFile.message}`);
        }
      },
      error: (err) => {
        console.error('Error deleting file:', err);
        modalInstance.showError($localize`Error while deleting the file`);
      },
    });
  }

  deleteFile(filePath: string) {
    return this.fileService.deleteFile(filePath);
  }

  ngOnDestroy(): void {
    if (this.uploadCompleteSubscription) {
      this.uploadCompleteSubscription.unsubscribe();
    }
  }
}
